const formatMoney = (cents, currency = 'MXN') => new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(cents || 0) / 100);

function calibrateGridImage(image) {
  if (image.dataset.ulCalibrated) return;
  try {
    const sampleWidth = Math.min(420, image.naturalWidth);
    const sampleHeight = Math.max(1, Math.round(image.naturalHeight * sampleWidth / image.naturalWidth));
    const canvas = document.createElement('canvas');
    canvas.width = sampleWidth;
    canvas.height = sampleHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return;
    context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
    const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
    const corners = [[2, 2], [sampleWidth - 3, 2], [2, sampleHeight - 3], [sampleWidth - 3, sampleHeight - 3]];
    const cornerSamples = corners.map(([x, y]) => {
      const offset = (y * sampleWidth + x) * 4;
      return [pixels[offset], pixels[offset + 1], pixels[offset + 2], pixels[offset + 3]];
    });
    const background = cornerSamples.reduce((sum, sample) => [
      sum[0] + sample[0], sum[1] + sample[1], sum[2] + sample[2]
    ], [0, 0, 0]).map((value) => value / corners.length);
    const transparentBackground = cornerSamples.reduce((sum, sample) => sum + sample[3], 0) / corners.length < 32;
    const backgroundLuma = background[0] * .2126 + background[1] * .7152 + background[2] * .0722;
    let minX = sampleWidth;
    let maxX = 0;
    let maxY = 0;
    let found = false;
    for (let y = 0; y < sampleHeight; y += 2) {
      for (let x = 0; x < sampleWidth; x += 2) {
        const offset = (y * sampleWidth + x) * 4;
        const red = pixels[offset];
        const green = pixels[offset + 1];
        const blue = pixels[offset + 2];
        const luma = red * .2126 + green * .7152 + blue * .0722;
        const distance = Math.hypot(red - background[0], green - background[1], blue - background[2]);
        const isSubject = transparentBackground
          ? pixels[offset + 3] > 32
          : pixels[offset + 3] > 180 && distance > 38 && luma < Math.min(225, backgroundLuma - 16);
        if (isSubject) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
          found = true;
        }
      }
    }
    if (!found || maxX <= minX) return;
    const box = image.getBoundingClientRect();
    const fittedScale = Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight);
    const fittedTop = (box.height - image.naturalHeight * fittedScale) / 2;
    const visibleWidth = (maxX - minX) / sampleWidth * image.naturalWidth * fittedScale;
    const visibleBottom = fittedTop + maxY / sampleHeight * image.naturalHeight * fittedScale;
    const scale = Math.min(1.42, Math.max(.9, box.width * .82 / Math.max(visibleWidth, 1)));
    const shift = box.height * .91 - visibleBottom * scale;
    image.style.transformOrigin = 'center top';
    image.style.transform = `matrix(${scale}, 0, 0, ${scale}, 0, ${shift})`;
    image.dataset.ulCalibrated = 'true';
  } catch {
    image.dataset.ulCalibrated = 'fallback';
  }
}

function initGridImages(scope = document) {
  scope.querySelectorAll('[data-ul-grid-primary]').forEach((image) => {
    if (image.complete && image.naturalWidth) calibrateGridImage(image);
    else image.addEventListener('load', () => calibrateGridImage(image), { once: true });
  });
}

/* Deckard · techno industrial minimalista.
   96 BPM; dos compases de 4/4 = célula de 5.000 s exactos (32 semicorcheas).
   Los eventos se agendan contra el reloj de AudioContext con ventana de
   anticipación: setInterval solo despierta al planificador, nunca marca el
   tiempo musical. Sin intro, sin crescendo, sin delay ni reverb, sin drone. */
const deckardHoverSound = (() => {
  const BPM = 96;
  const SIXTEENTH = 60 / BPM / 4;        // 0.15625 s
  const CELL_STEPS = 32;                 // 5.000 s
  const LOOKAHEAD = 0.12;                // s de anticipación
  const TICK = 25;                       // ms entre despertares del planificador

  // Compases: el primero establece, el segundo repite con variación metálica.
  const KICK  = [0, 4, 8, 12, 16, 20, 24, 28];
  const BASS  = [6, 14, 22, 30];         // sincopado, nunca encima del kick
  const METAL_A = [3, 11];
  const METAL_B = [19, 25];              // variación del segundo compás
  const CRACK = [13, 27];                // textura en ambos compases
  const ANVIL = [16];                    // clang que abre el segundo compás

  let context;
  let master;
  let noiseBuffer;
  let active;

  const getContext = () => {
    if (context) return context;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    context = new AudioContext();

    master = context.createGain();
    master.gain.value = 0.9;
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 8;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    master.connect(limiter).connect(context.destination);

    // Ruido reutilizable: evita reservar un buffer por evento.
    noiseBuffer = context.createBuffer(1, context.sampleRate, context.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;

    return context;
  };

  const unlock = () => {
    const audioContext = getContext();
    if (audioContext?.state === 'suspended') audioContext.resume().catch(() => {});
  };

  /* Saturación cálida muy leve para el bajo. */
  const makeDrive = () => {
    const shaper = context.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < curve.length; i += 1) {
      const x = (i * 2) / (curve.length - 1) - 1;
      curve[i] = Math.tanh(x * 2.7);
    }
    shaper.curve = curve;
    shaper.oversample = '2x';
    return shaper;
  };

  const panned = (amount) => {
    if (!context.createStereoPanner) return master;
    const panner = context.createStereoPanner();
    panner.pan.value = amount;
    panner.connect(master);
    return panner;
  };

  /* Envolvente que siempre aterriza en cero: sin clics al cerrar el loop. */
  const envelope = (gain, time, peak, attack, release) => {
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peak, time + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + release);
    gain.gain.setValueAtTime(0, time + release + 0.005);
  };

  /* 1. Kick: grave seco, caída breve de frecuencia, decay corto. */
  const kick = (time) => {
    const osc = context.createOscillator();
    const gain = context.createGain();
    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 220;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(124, time);
    osc.frequency.exponentialRampToValueAtTime(44, time + 0.045);
    envelope(gain, time, 0.95, 0.004, 0.13);
    osc.connect(gain).connect(lowpass).connect(master);
    osc.start(time);
    osc.stop(time + 0.2);

    // Transiente corto y agudo sobre el ataque: el golpe se vuelve físico.
    const click = context.createBufferSource();
    const clickGain = context.createGain();
    const highpass = context.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 1400;
    click.buffer = noiseBuffer;
    click.loop = true;
    envelope(clickGain, time, 0.17, 0.001, 0.013);
    click.connect(clickGain).connect(highpass).connect(master);
    click.start(time);
    click.stop(time + 0.05);
  };

  /* 2. Bajo: seno profundo, mono, saturación leve, respuesta breve. */
  const bass = (time, weight) => {
    const osc = context.createOscillator();
    const gain = context.createGain();
    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 170;
    lowpass.Q.value = 0.8;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(55, time);
    envelope(gain, time, 0.5 * weight, 0.01, 0.165);
    osc.connect(gain).connect(makeDrive()).connect(lowpass).connect(master);
    osc.start(time);
    osc.stop(time + 0.24);
  };

  /* 3. Metal: ticks pequeños y espaciados, dos timbres discretos. */
  const metal = (time, variant, brightness, accent = 1) => {
    // Parciales inarmónicos: suena a chapa golpeada, no a nota.
    const base = variant === 0 ? 2100 : 2750;
    const ratios = [1, 1.41, 1.93];
    const destination = panned(variant === 0 ? -0.22 : 0.24);
    const band = context.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = base * 1.25;
    band.Q.value = 2.1;
    band.connect(destination);

    ratios.forEach((ratio, index) => {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(base * ratio, time);
      envelope(gain, time, (0.13 * brightness * accent) / (index + 1.4), 0.001, variant === 0 ? 0.055 : 0.038);
      osc.connect(gain).connect(band);
      osc.start(time);
      osc.stop(time + 0.1);
    });
  };

  /* Acento: misma chapa, más grave y con algo más de cola. */
  const anvil = (time, brightness) => {
    const ratios = [1, 1.47, 2.11];
    const destination = panned(-0.12);
    const band = context.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1500;
    band.Q.value = 1.6;
    band.connect(destination);

    ratios.forEach((ratio, index) => {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(900 * ratio, time);
      envelope(gain, time, (0.2 * brightness) / (index + 1.3), 0.001, 0.13);
      osc.connect(gain).connect(band);
      osc.start(time);
      osc.stop(time + 0.18);
    });
  };

  /* 4. Grieta eléctrica: ráfaga corta de ruido filtrado entre los graves. */
  const crack = (time, texture) => {
    const source = context.createBufferSource();
    const gain = context.createGain();
    const band = context.createBiquadFilter();
    source.buffer = noiseBuffer;
    source.loop = true;
    band.type = 'bandpass';
    band.Q.value = 1.3;
    band.frequency.setValueAtTime(1700, time);
    band.frequency.exponentialRampToValueAtTime(3800, time + 0.07);
    envelope(gain, time, 0.13 * texture, 0.003, 0.075);
    source.connect(gain).connect(band).connect(panned(0.3));
    source.start(time);
    source.stop(time + 0.12);
  };

  const scheduleStep = (step, time, options) => {
    if (KICK.includes(step)) kick(time);
    if (BASS.includes(step)) bass(time, options.bassWeight);
    if (METAL_A.includes(step)) metal(time, 0, options.brightness);
    if (METAL_B.includes(step)) metal(time, 1, options.brightness);
    if (ANVIL.includes(step)) anvil(time, options.brightness);
    if (CRACK.includes(step)) crack(time, options.texture);
    // 5. Silencio: el resto de las semicorcheas quedan vacías a propósito.
  };

  const start = () => {
    if (window.matchMedia('(hover: none), (pointer: coarse), (prefers-reduced-motion: reduce)').matches) return;
    const audioContext = getContext();
    if (!audioContext || audioContext.state !== 'running' || active) return;

    const options = { bassWeight: 1, brightness: 1, texture: 1 };
    let step = 0;
    let nextTime = audioContext.currentTime + 0.06;

    const schedule = () => {
      while (nextTime < audioContext.currentTime + LOOKAHEAD) {
        scheduleStep(step % CELL_STEPS, nextTime, options);
        nextTime += SIXTEENTH;
        step += 1;
      }
    };

    master.gain.cancelScheduledValues(audioContext.currentTime);
    master.gain.setValueAtTime(0.9, audioContext.currentTime);
    schedule();
    active = { timer: window.setInterval(schedule, TICK) };
  };

  const stop = () => {
    if (!active || !context) return;
    const voice = active;
    active = null;
    window.clearInterval(voice.timer);
    // Cierre corto: corta sin golpe, pero no es un fade musical.
    const now = context.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), now);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
  };

  document.addEventListener('pointerdown', unlock, { passive: true });
  document.addEventListener('keydown', unlock);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
  });

  return { start, stop };
})();

function initDeckardHoverSound(scope = document) {
  scope.querySelectorAll('[data-product-handle="deckard-biker-boots"]').forEach((card) => {
    if (card.dataset.ulDeckardSoundReady === 'true') return;
    card.dataset.ulDeckardSoundReady = 'true';
    card.addEventListener('mouseenter', deckardHoverSound.start);
    card.addEventListener('mouseleave', deckardHoverSound.stop);
    card.addEventListener('focusin', deckardHoverSound.start);
    card.addEventListener('focusout', deckardHoverSound.stop);
  });
}

function initHeaderReveal() {
  const headerGroup = document.querySelector('#header-group');
  const header = headerGroup?.querySelector('.ul-header');
  const pageWrapper = document.querySelector('.page-wrapper');
  if (!headerGroup || !header || headerGroup.dataset.ulRevealReady === 'true') return;
  headerGroup.dataset.ulRevealReady = 'true';

  const getScrollTop = () => {
    if (pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1) return pageWrapper.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  };
  let previousTop = getScrollTop();
  let ticking = false;

  const update = () => {
    const currentTop = getScrollTop();
    const delta = currentTop - previousTop;
    const headerIsOpen = header.matches(':focus-within') || header.querySelector('[aria-expanded="true"]');
    if (currentTop <= 8 || delta < -2 || headerIsOpen) headerGroup.classList.remove('ul-header-group--hidden');
    else if (delta > 4 && currentTop > header.offsetHeight) headerGroup.classList.add('ul-header-group--hidden');
    previousTop = currentTop;
    ticking = false;
  };
  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  pageWrapper?.addEventListener('scroll', requestUpdate, { passive: true });
}

function initProduct(root) {
  if (root.dataset.ulReady === 'true') return;
  root.dataset.ulReady = 'true';
  const variants = JSON.parse(root.querySelector('[data-ul-variants]')?.textContent || '[]');
  const idInput = root.querySelector('[data-ul-variant-id]');
  const price = root.querySelector('[data-ul-price]');
  const discountBadge = root.querySelector('[data-ul-discount-badge]');
  const add = root.querySelector('[data-ul-add]');
  const mtoAdd = root.querySelector('[data-ul-mto-add]');
  const mtoPanel = root.querySelector('[data-ul-mto-panel]');
  const mtoProperty = root.querySelector('[data-ul-mto-property]');
  const deliveryEstimate = root.querySelector('[data-ul-delivery-estimate]');
  const deliveryRange = root.querySelector('[data-ul-delivery-range]');
  const currency = window.Shopify?.currency?.active || 'MXN';
  const addBusinessDays = (date, days) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const weekday = result.getDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }
    return result;
  };
  const formatDeliveryDate = (date) => new Intl.DateTimeFormat('es-MX', {
    day: 'numeric', month: 'short'
  }).format(date).replace('.', '').toLowerCase();
  const addCalendarDays = (date, days) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + days);
    return result;
  };
  const updateDeliveryEstimate = (isMto = false) => {
    if (!deliveryEstimate || !deliveryRange) return;
    const today = new Date();
    const minDays = Number(isMto ? deliveryEstimate.dataset.mtoMinDays || 21 : deliveryEstimate.dataset.minBusinessDays || 3);
    const maxDays = Number(isMto ? deliveryEstimate.dataset.mtoMaxDays || 25 : deliveryEstimate.dataset.maxBusinessDays || 5);
    const addDays = isMto ? addCalendarDays : addBusinessDays;
    deliveryRange.textContent = `${formatDeliveryDate(addDays(today, minDays))} - ${formatDeliveryDate(addDays(today, maxDays))}`;
    deliveryEstimate.hidden = false;
  };
  // Sin una sola talla en stock el unico camino es made to order: el plazo que
  // se muestra al cargar tiene que ser el de fabricacion, no el de almacen.
  updateDeliveryEstimate(deliveryEstimate?.dataset.soloBajoPedido === 'true');
  const select = (button) => {
    const variant = variants.find((item) => String(item.id) === button.dataset.ulVariant);
    if (!variant) return;
    root.querySelectorAll('[data-ul-variant]').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.ulVariant === button.dataset.ulVariant);
    });
    idInput.value = variant.id;
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
    const isOnSale = variant.compare_at_price > variant.price;
    const compare = isOnSale ? `<s>${formatMoney(variant.compare_at_price, currency)}</s>` : '';
    price.classList.toggle('is-on-sale', isOnSale);
    price.innerHTML = `<strong>${formatMoney(variant.price, currency)}</strong>${compare}`;
    if (discountBadge) {
      const discountPercentage = isOnSale ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
      discountBadge.hidden = discountPercentage < 1;
      discountBadge.textContent = discountPercentage > 0 ? `-${discountPercentage}%` : '';
    }
    const isMto = button.dataset.ulMto === 'true';
    add.disabled = isMto || !variant.available;
    if (mtoAdd) mtoAdd.disabled = !isMto || !variant.available;
    if (mtoPanel) {
      mtoPanel.hidden = !isMto;
      mtoPanel.open = isMto;
    }
    if (mtoProperty) mtoProperty.disabled = !isMto;
    updateDeliveryEstimate(isMto);
  };
  root.querySelectorAll('[data-ul-variant]').forEach((button) => button.addEventListener('click', () => select(button)));
}

function initAll(scope = document) {
  initHeaderReveal();
  initGridImages(scope);
  initDeckardHoverSound(scope);
  scope.querySelectorAll('[data-ul-product]').forEach(initProduct);
  scope.querySelectorAll('.ul-product__media--video video').forEach((video) => {
    video.muted = true; video.defaultMuted = true; video.loop = true; video.playsInline = true; video.controls = false;
    video.play().catch(() => {});
  });
}
document.addEventListener('DOMContentLoaded', () => initAll());
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
